import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import AppSidebar from '../components/admin/AppSidebar';
import PreprodBanner, { isPreprodHost } from '../components/PreprodBanner';
import AppTopbar, { PageCrumbProvider } from '../components/admin/AppTopbar';
import SidebarDrawer from '../components/admin/SidebarDrawer';
import { isInIframe } from '../utils/deviceUtils';

const DESKTOP_QUERY = '(min-width: 1024px)';

/**
 * Subscribes to a media query and returns its unsubscribe.
 * Safari gained `addEventListener` on MediaQueryList only in 14, and `npx browserslist`
 * still resolves `ios_saf 11.0-11.2` here — calling it there throws and unmounts the shell.
 */
const watchMedia = (query, onChange) => {
  const mql = window.matchMedia(query);
  onChange(mql);

  if (typeof mql.addEventListener === 'function') {
    const handler = () => onChange(mql);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }

  const legacyHandler = () => onChange(mql);
  mql.addListener(legacyHandler);
  return () => mql.removeListener(legacyHandler);
};

const AdminLayout = ({ children, username, email, onLogout }) => {
  const embedded = isInIframe();
  const { pathname } = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  // Any route change closes the drawer, a browser Back included.
  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  // Above the breakpoint the fixed sidebar takes over, so the overlay must go: otherwise the
  // scrim and the focus trap survive the resize.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;

    return watchMedia(DESKTOP_QUERY, (desktop) => {
      if (desktop.matches) setNavOpen(false);
    });
  }, []);

  // Embedded in an iframe the host provides its own chrome: no sidebar, no topbar.
  if (embedded) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <main className="flex-1">{children}</main>
      </div>
    );
  }

  const sidebarProps = { username, email, onLogout };

  return (
    // The banner is fixed full-width ON TOP of the sidebar; the var pushes sidebar/topbar below it
    <div className={`min-h-screen bg-gray-50 dark:bg-zinc-900 pt-[var(--preprod-banner-height,0px)] ${isPreprodHost() ? '[--preprod-banner-height:24px]' : ''}`}>
      {isPreprodHost() && (
        <div className="fixed inset-x-0 top-0 z-40">
          <PreprodBanner />
        </div>
      )}
      <AppSidebar {...sidebarProps} className="fixed top-[var(--preprod-banner-height,0px)] bottom-0 left-0 z-30 hidden lg:flex" />

      <SidebarDrawer open={navOpen} onOpenChange={setNavOpen}>
        <AppSidebar {...sidebarProps} className="h-full" onNavigate={() => setNavOpen(false)} />
      </SidebarDrawer>

      {/* Around the topbar and the page both: a record page names its own last crumb. */}
      <PageCrumbProvider>
        <div className="flex min-h-screen flex-col lg:pl-[248px]">
          <AppTopbar onOpenNav={() => setNavOpen(true)} />
          <main className="mx-auto w-full max-w-[1320px] flex-1 overflow-x-auto">{children}</main>
        </div>
      </PageCrumbProvider>
    </div>
  );
};

AdminLayout.propTypes = {
  children: PropTypes.node.isRequired,
  username: PropTypes.string.isRequired,
  email: PropTypes.string,
  onLogout: PropTypes.func.isRequired,
};

export default AdminLayout;
